import { FC } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import { usePerformanceMeasure } from '@ucc/unified-admin-common'
import FlagProvider from '@unleash/proxy-client-react'

import {
  AppErrorBoundary,
  AppErrorProvider,
  BaseRoutes,
  ConnectedUserProvider,
  FeatureFlagProvider,
  GetGoAuthProvider,
  GlobalHighlightedElementProvider,
  GlobalNotificationsProvider,
  GlobalStateInitializer,
  LanguageProvider,
  Layout,
  LiveJobsProvider,
  OrganizationProvider,
  RegionsProvider,
  Routes,
  SnackbarProvider,
  TopBarBannerProvider,
} from 'components'
import config from 'config'
import { useLocale, useWrapperUnleashConfig } from 'hooks'
import useCentralizedMixpanel from 'hooks/useCentralizedMixpanel'
import { apolloClient } from 'utils'
import fetchAdmin from 'utils/fetchAdmin'
import * as logger from 'utils/logger/init'
import { AppErrorContextProvider } from './contexts/AppErrorContext'
import LoggerContextInitializer from './logger/LoggerContextInitializer'
import { PERFORMANCE_MARKS } from './logger/performanceConstants'
import AccountProvider from './providers/AccountProvider'
import AppsVersionProvider from './providers/AppsVersionProvider'
import AppThemeProvider from './providers/AppThemeProvider'
import ContactCenterUserProvider from './providers/ContactCenterUserProvider'
import { DefaultPageProvider } from './providers/DefaultPageProvider'
import PendoProvider from './providers/PendoProvider'
import UnleashProvider from './providers/UnleashProvider'

import '@getgo/chameleon-web/components/registerAll'
logger

// Creating a global fetch method to handle 401s
// that sub-applications can decide to opt-in
global.fetchAdmin = fetchAdmin

interface AppProps {
  loading: boolean
}

const App: FC<AppProps> = ({ loading }) => {
  const locale = useLocale()
  const { markOnFirstMount } = usePerformanceMeasure()
  const unleashConfig = useWrapperUnleashConfig()

  markOnFirstMount(PERFORMANCE_MARKS.WRAPPER_INITIAL_LOAD_START_MARK)

  useCentralizedMixpanel()

  return (
    <GetGoAuthProvider>
      <LanguageProvider locale={locale}>
        <AppThemeProvider>
          <AppErrorProvider>
            <ApolloProvider client={apolloClient}>
              <Router basename={config.APP_URL_PREFIX}>
                <AppErrorContextProvider>
                  <AccountProvider>
                    <LiveJobsProvider>
                      <SnackbarProvider>
                        <GlobalNotificationsProvider>
                          <GlobalHighlightedElementProvider>
                            <FeatureFlagProvider>
                              <RegionsProvider>
                                <OrganizationProvider>
                                  <FlagProvider config={unleashConfig}>
                                    <ConnectedUserProvider>
                                      <UnleashProvider>
                                        <DefaultPageProvider>
                                          <AppErrorBoundary>
                                            <AppsVersionProvider>
                                              <ContactCenterUserProvider>
                                                <PendoProvider>
                                                  <TopBarBannerProvider>
                                                    <GlobalStateInitializer locale={locale}>
                                                      <LoggerContextInitializer>
                                                        <BaseRoutes>
                                                          <Layout loading={loading}>
                                                            <Routes />
                                                          </Layout>
                                                        </BaseRoutes>
                                                      </LoggerContextInitializer>
                                                    </GlobalStateInitializer>
                                                  </TopBarBannerProvider>
                                                </PendoProvider>
                                              </ContactCenterUserProvider>
                                            </AppsVersionProvider>
                                          </AppErrorBoundary>
                                        </DefaultPageProvider>
                                      </UnleashProvider>
                                    </ConnectedUserProvider>
                                  </FlagProvider>
                                </OrganizationProvider>
                              </RegionsProvider>
                            </FeatureFlagProvider>
                          </GlobalHighlightedElementProvider>
                        </GlobalNotificationsProvider>
                      </SnackbarProvider>
                    </LiveJobsProvider>
                  </AccountProvider>
                </AppErrorContextProvider>
              </Router>
            </ApolloProvider>
          </AppErrorProvider>
        </AppThemeProvider>
      </LanguageProvider>
    </GetGoAuthProvider>
  )
}

export default App
